/* eslint-disable prettier/prettier */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useRef, useEffect } from 'react';
import { Button, useGlobal, SelectCustom } from '@rcg/ui-react';
import { formatPrice } from '@rcg/pricing-format';
import {
  Box,
  Card,
  Divider,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AccordionActions,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Formik } from 'formik';
import { usePaymentInputs } from 'react-payment-inputs';
import getSymbolFromCurrency from 'currency-symbol-map';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import images from 'react-payment-inputs/images';
import clsx from 'clsx';
import {
  setupPaywayInterval,
  runGatewayPaywayValidation,
  handlePaymentApiAndThreeDs,
} from '../../commons/3dsValidation';
import paymentValidationSchema from './index.validation';
import { CC_TYPE,SESSION_STORAGE_CURRENT_BOOKING } from '../../commons/constants';
import useStyles from './index.style';
import TextField from '../TextField';
import Loader from '../Loader';
import { trackPaymentCTA } from '../../commons';
import theme from '../../theme/booking-theme';
import { onlyLetters,getSessionStorageItem } from '../../commons/utils';

const MakePayment = ({
  isMobile,
  rooms,
  setMakePaymentInfo,
  paymentResponse,
  paymentErrorInfo,
  currentBooking,
  showWarningNotification,
  showErrorNotification,
  royalPayway,
  price: { balanceDue, balanceDueDate, currencyCode, inFinal },
  payment3dsToggle,
}) => {
  const {
    renderContent,
    data: { country, localizedDayjs },
  } = useGlobal();
  const classes = useStyles();
  const [value, setValue] = useState('');
  const [showPanel, setShowPanel] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [activeRoom, setActiveRoom] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  const [payResponse, setPayResponse] = useState(balanceDue);
  const [showLoader, setShowLoader] = useState(false);
  const [month, setMonth] = React.useState('none');
  const [year, setYear] = React.useState('none');
  const [expiryDateErrors, setExpiryDateErrors] = useState(false);
  const { meta, getCardNumberProps, getCardImageProps } = usePaymentInputs();
  const cardNum = useRef(null);

  const placeholderColor = theme.palette.grey8;
  const activeColor = theme.palette.grey1;

  useEffect(() => {
    const currentBookingInfo = getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING);
    if (paymentResponse) {
      const payment = paymentResponse.makePayment;
      trackPaymentCTA(
        { amount: payment.paymentAmount, remainingBalance: payment.remainingBalance },
        '',
        'success',
        currentBookingInfo
      );
      setShowLoader(false);
      setPayResponse(payment.remainingBalance);
    }
    if (paymentErrorInfo) {      
      trackPaymentCTA({}, paymentErrorInfo, 'error',currentBookingInfo);
      setShowLoader(false);
    }
  }, [paymentResponse, paymentErrorInfo]);

  const radioHandleChange = () => (event) => {
    setValue(event.target.value);
    setShowPanel(true);

    if (event.target.value === 'remainingBalance' || rooms.length > 1) {
      setShowInput(false);
    } else {
      setShowInput(true);
    }
  };

  const handleInput = (handleChange) => (e) => {
    const stringAmount = e.target.value.replace(/,/g, '');
    setCustomAmount(+stringAmount);
    handleChange(e.target.name)(e.target.value);
  };

  const handleActiveRoom = (event) => {
    let roomId;
    if (event.target.type === 'number') {
      const element = event.target.getAttribute('aria-label');
      roomId = +element;
    } else {
      roomId = +event.target.dataset.id;
    }
    setActiveRoom(roomId);
  };

  const numberMask = createNumberMask({
    prefix: '',
    suffix: '',
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ',',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 2,
    integerLimit: 7,
    allowNegative: false,
    allowLeadingZeroes: false,
  });

  useEffect(() => {
    if (payment3dsToggle && royalPayway) {
      setupPaywayInterval(royalPayway);
    }
    // eslint-disable-next-line
  }, [payment3dsToggle]);

  const years = Array.from({ length: 21 }, (v, index) => index + localizedDayjs().year()).map(
    (item) => {
      return { label: item, value: String(item) };
    },
  );

  const months = localizedDayjs
    .localeData()
    .monthsShort()
    .map((monthShort, i) => {
      return {
        label: monthShort,
        value: i < 9 ? `${0}${i + 1}` : `${i + 1}`,
      };
    });

  const monthsHandler = (e) => {
    setMonth(e.target.value);
  };

  const yearsHandler = (e) => {
    setYear(e.target.value);
  };

  useEffect(() => {
    if (month && year) {
      const currentDate = new Date();
      const selectedDate = new Date();
      selectedDate.setMonth(Number(month) - 1);
      selectedDate.setYear(year);
      if (selectedDate < currentDate) {
        setExpiryDateErrors(true);
      } else {
        setExpiryDateErrors(false);
      }
    }
  }, [month, year]);

  return (
    <Card className={classes.rootCard}>
      <Box className={classes.container}>
        <Box>
          <Typography className={classes.heading}>
            {renderContent('paymentBookingPanelPaymentOpts', 'Payment Options')}
          </Typography>
        </Box>
        <Divider />
      </Box>
      <Formik
        initialValues={{
          firstNameOnCard: '',
          lastNameOnCard: '',
          cardNumber: '',
          expiryMonth: '',
          expiryYear: '',
          expiryDate: '',
          paymentValue: '',
          cardType: '',
          amount: '',
        }}
        validationSchema={paymentValidationSchema(renderContent, payResponse)}
        onSubmit={(values, actions) => {
          const amount =
            values.paymentValue === 'customAmount' ? Number(customAmount) : Number(payResponse);
          const cardNumber = values.cardNumber.toString().replace(/\s/g, '');
          const cardType = CC_TYPE[meta.cardType.type];
          const expiration = `${month}${year.slice(-2)}`;
          const variables = {
            variables: { ...values, amount, expiration, cardNumber, cardType },
          };

          setShowLoader(true);
          setShowPanel(false);
          setShowInput(false);

          if (payment3dsToggle && royalPayway) {
            runGatewayPaywayValidation({
              ...values,
              amount,
              variables,
              royalPayway,
              setShowLoader,
              country,
              month,
              year,
              currentBooking,
              handlePaymentApiAndThreeDs,
              showWarningNotification,
              showErrorNotification,
              setMakePaymentInfo,
            });
          } else {
            setShowLoader(true);
            handlePaymentApiAndThreeDs(
              null,
              variables,
              false,
              setShowLoader,
              setMakePaymentInfo,
              false,
            );
          }
          actions.resetForm();
          setCustomAmount('');
          cardNum.current.value = '';
          setMonth('none');
          setYear('none');
        }}
      >
        {(props) => (
          <form onSubmit={props.handleSubmit} data-testid="form" autoComplete="off">
            <Accordion className={classes.pannel} expanded={showPanel}>
              <AccordionSummary
                aria-controls="panel-content"
                id="panel-header"
                className={classes.summaryContent}>
                <Box width="100%">
                  <FormControl fullWidth>
                    <RadioGroup
                      data-testid="payment-radio-group"
                      aria-label="paymentValue"
                      name="paymentValue"
                      value={props.values.paymentValue || ''}
                      onChange={radioHandleChange()}>
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        className={classes.details}>
                        <Grid item sm={6} xs={12}>
                          <Typography className={classes.detailsTitle}>
                            {payResponse > 0
                              ? renderContent(
                                  `paymentBookingPanelPayRemainingBalance`,
                                  `Pay Remaining Balance`,
                                )
                              : renderContent('paymentBookingAmendPaidInFull', 'Paid in Full')}
                          </Typography>
                          {payResponse > 0 && (
                            <Typography className={classes.detailsSubtitle}>
                              {renderContent('paymentBookingAmendDueLabel', 'Due')}{' '}
                              {localizedDayjs(balanceDueDate).format('MMM D, YYYY')}
                            </Typography>
                          )}
                        </Grid>
                        {payResponse > 0 && (
                          <>
                            <Grid item sm={6} xs={12}>
                              <Typography data-testid="balance-due" className={classes.amount}>
                                {formatPrice(payResponse, currencyCode, true, true, true)}
                              </Typography>
                            </Grid>
                            <FormControlLabel
                              value="remainingBalance"
                              control={(
                                <Radio
                                  onChange={props.handleChange}
                                  inputProps={{ 'data-testid': 'remaining-balance-radio' }}
                                  className={classes.radioButton}
                                />
                              )}
                            />
                          </>
                        )}
                      </Grid>
                      {payResponse > 0 && !inFinal && (
                        <Grid
                          container
                          direction="row"
                          alignItems="center"
                          className={classes.details}>
                          <Grid item sm={6} xs={12}>
                            <Typography className="detailsTitle">
                              {renderContent('paymentBookingPanelCustomAmt', 'Pay Custom Amount')}
                            </Typography>
                          </Grid>
                          <Grid item sm={6} xs={12} className={classes.customInput}>
                            {showInput && (
                              <div>
                                <MaskedInput
                                  defaultValue=""
                                  autoFocus
                                  mask={numberMask}
                                  name="amount"
                                  onBlur={props.handleBlur}
                                  onChange={handleInput(props.handleChange)}
                                  error={props.errors.amount && props.touched.amount}
                                  value={props.values.amount}
                                  inputProps={{ 'data-testid': 'custom-amount' }}
                                  startAdornment={(
                                    <InputAdornment position="start">
                                      {getSymbolFromCurrency(currencyCode)}
                                    </InputAdornment>
                                  )}
                                  render={(ref, propsMasked) => {
                                    return <Input inputRef={ref} {...propsMasked} />;
                                  }}
                                />
                                <FormHelperText
                                  error={
                                    props.errors && props.touched.amount && props.errors.amount
                                  }>
                                  {props.errors.amount}
                                </FormHelperText>
                              </div>
                            )}
                          </Grid>
                          <FormControlLabel
                            value="customAmount"
                            control={(
                              <Radio
                                onChange={props.handleChange}
                                inputProps={{ 'data-testid': 'custom-amount-radio' }}
                                className={classes.radioButton}
                              />
                            )}
                          />
                        </Grid>
                      )}
                    </RadioGroup>
                  </FormControl>
                </Box>
              </AccordionSummary>
              <AccordionDetails className={classes.Accordion}>
                {rooms.length > 1 && value === 'customAmount' && (
                  <div>
                    <Grid container>
                      {rooms.map((room, index) => {
                        return (
                          <Card
                            classes={{ root: classes.card }}
                            data-testid="room-card"
                            key={rooms[index]}
                            data-id={index}
                            onClick={handleActiveRoom}
                            className={clsx(
                              `${classes.roomRemaining}`,
                              activeRoom === index ? 'roomActive' : 'roomInActive',
                            )}>
                            <Typography
                              data-id={index}
                              className={clsx(
                                `${classes.headingRoom}`,
                                activeRoom === index
                                  ? `${classes.headingActive}`
                                  : `${classes.headingInActive}`,
                              )}>
                              {renderContent('formBookingAmendRoomLabel', 'Room')}
                              {rooms[index]}
                            </Typography>
                            <FormControl className={classes.formControl} data-id={index}>
                              <Input
                                data-id={index}
                                defaultValue=""
                                type="number"
                                className={classes.textField}
                                autoFocus={rooms[index] === 1}
                                inputProps={{
                                  'aria-label': `${index}`,
                                  'data-testid': `room${index}`,
                                }}
                              />
                              <FormHelperText
                                data-id={index}
                                id="component-helper-text"
                                className={classes.helper}>
                                $1.234.56
                                {renderContent('paymentBookingAmendRemaining', 'Remaining')}
                              </FormHelperText>
                            </FormControl>
                          </Card>
                        );
                      })}
                    </Grid>
                    <Divider className={classes.divider} />
                  </div>
                )}

                {payResponse > 0 && (
                  <Box>
                    <Typography className={classes.heading}>
                      {renderContent('paymentBookingCreditCardTitle', 'Credit Card Information')}
                    </Typography>
                    <Divider />
                    <Box className={classes.fieldsContainer}>
                      <Grid container>
                        <Grid container>
                          <Grid item xs={12} md={6} lg={4}>
                            <MaskedInput
                              mask={onlyLetters}
                              data-testid="first-name"
                              label={renderContent(
                                'formBookingAmendFieldFirstNameLabel',
                                'First Name',
                              )}
                              placeholder={renderContent(
                                'formBookingAmendFieldFirstNameLabel',
                                'First Name',
                              )}
                              name="firstNameOnCard"
                              onBlur={props.handleBlur}
                              onChange={props.handleChange}
                              error={props.errors.firstNameOnCard && props.touched.firstNameOnCard}
                              helperText={
                                props.errors &&
                                props.touched.firstNameOnCard &&
                                props.errors.firstNameOnCard
                              }
                              value={props.values.firstNameOnCard}
                              inputProps={{
                                'data-testid': 'first-name-on-card',
                                className: `${classes.textFieldField}`,
                              }}
                              render={(ref, propsMasked) => {
                                return <TextField inputRef={ref} {...propsMasked} />;
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} lg={4}>
                            <MaskedInput
                              mask={onlyLetters}
                              label={renderContent(
                                'formBookingAmendFieldLastNameLabel',
                                'Last name',
                              )}
                              placeholder={renderContent(
                                'formBookingAmendFieldLastNameLabel',
                                'Last name',
                              )}
                              name="lastNameOnCard"
                              onBlur={props.handleBlur}
                              onChange={props.handleChange}
                              error={props.errors.lastNameOnCard && props.touched.lastNameOnCard}
                              helperText={
                                props.errors &&
                                props.touched.lastNameOnCard &&
                                props.errors.lastNameOnCard
                              }
                              value={props.values.lastNameOnCard}
                              inputProps={{
                                'data-testid': 'last-name-on-card',
                                className: `${classes.textFieldField}`,
                              }}
                              render={(ref, propsMasked) => {
                                return <TextField inputRef={ref} {...propsMasked} />;
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Grid container className={classes.rowContainer}>
                          <Grid item xs={12} md={6} lg={4} className="cardNumber">
                            <svg {...getCardImageProps({ images })} />
                            <TextField
                              data-testid="cardNumber"
                              InputLabelProps={{ className: classes.styledLabel, shrink: true }}
                              inputProps={{
                                ...getCardNumberProps({
                                  onChange: props.handleChange,
                                  onBlur: props.handleBlur,
                                }),
                                placeholder: renderContent(
                                  'formBookingAmendFieldCreditCardPlaceholder',
                                  'Card number',
                                ),
                                maxLength: 19,
                                'data-testid': 'card-number',
                                className: `${classes.textFieldField}`,
                              }}
                              inputRef={cardNum}
                              label={renderContent('paymentBookingAmendCardNumber', 'Card Number')}
                              placeholder={renderContent(
                                'formBookingAmendFieldCreditCardPlaceholder',
                                'Card number',
                              )}
                              name="cardNumber"
                              error={props.errors.cardNumber && props.touched.cardNumber}
                              helperText={
                                props.errors && props.touched.cardNumber && props.errors.cardNumber
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={6} lg={4} className={classes.dateSelects}>
                            <SelectCustom
                              isMobile={isMobile}
                              selectLabel={renderContent(
                                'paymentBookingPanelExpires',
                                'Expiration date',
                              )}
                              placeholder={renderContent(
                                'paymentBookingExpireMonthPlaceholder',
                                'Month',
                              )}
                              options={months}
                              menuClassName={classes.selectMenu}
                              defaultColor={month === 'none' ? placeholderColor : activeColor}
                              handleOption={monthsHandler}
                              currentValue={month}
                              dataTestid="expiration-month"
                              textHelperError={
                                expiryDateErrors
                                  ? renderContent(
                                      'formBookingAmendRequiredExpireDate',
                                      'Invalid date',
                                    )
                                  : ''
                              }
                            />
                            <SelectCustom
                              isMobile={isMobile}
                              selectLabel="&nbsp;"
                              placeholder={renderContent(
                                'paymentBookingExpireYearPlaceholder',
                                'Year',
                              )}
                              options={years}
                              menuClassName={classes.selectMenu}
                              defaultColor={year === 'none' ? placeholderColor : activeColor}
                              handleOption={yearsHandler}
                              currentValue={year}
                              dataTestid="expiration-year"
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
              <AccordionActions className={classes.panelActions}>
                <Button
                  color="primary"
                  className={classes.button}
                  data-testid="submit-button"
                  disabled={
                    !props.isValid ||
                    typeof meta.error !== 'undefined' ||
                    expiryDateErrors ||
                    year === 'none' ||
                    month === 'none'
                  }
                  fullWidth
                  type="submit"
                  variant="contained">
                  <Box data-testid="payment-button">
                    {renderContent('paymentBookingPanelPay', 'Pay ')}{' '}
                    {formatPrice(
                      value === 'remainingBalance' ? +payResponse : +customAmount,
                      currencyCode,
                      true,
                      true,
                      true,
                    )}
                  </Box>
                </Button>
              </AccordionActions>
            </Accordion>
          </form>
        )}
      </Formik>
      <Loader display={showLoader} />
    </Card>
  );
};

export default MakePayment;
